import { useRef, useState } from "react";
import { Upload, Image as ImageIcon, X, FileVideo, FileAudio, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, BACKEND_URL, formatError } from "@/lib/api";

/**
 * FileUpload — drag/drop OR file-picker uploader for admin forms.
 *
 * Props:
 *  - value: current URL (relative /uploads/... or absolute http(s)://)
 *  - onChange(url): called with the uploaded URL after success
 *  - accept: file accept attribute (defaults to "image/*")
 *  - label: optional field label
 *  - kind: "image" | "video" | "audio" | "file" (for icon)
 *  - allowUrl: also allow pasting a remote URL (default true)
 */
export default function FileUpload({ value, onChange, accept = "image/*", label, kind = "image", allowUrl = true }) {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [urlInput, setUrlInput] = useState("");

  const fullUrl = (u) => {
    if (!u) return "";
    if (u.startsWith("http")) return u;
    return `${BACKEND_URL}${u}`;
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Uploaded");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) upload(file);
  };

  const Icon = kind === "video" ? FileVideo : kind === "audio" ? FileAudio : kind === "file" ? FileText : ImageIcon;
  const isImage = kind === "image";
  const showPreview = isImage && value;

  return (
    <div>
      {label && <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 block">{label}</label>}
      <div
        className={`mt-2 border-2 border-dashed p-3 transition-colors ${
          dragOver ? "border-[#E65C00] bg-[#E65C00]/5" : "border-gray-300"
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        data-testid={`fileupload-${label || kind}`}
      >
        {showPreview ? (
          <div className="flex items-center gap-3">
            <img src={fullUrl(value)} alt="preview" className="w-20 h-20 object-cover border border-gray-200" />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-gray-500 truncate">{value}</div>
              <div className="flex gap-2 mt-2">
                <button type="button" onClick={() => ref.current?.click()} className="text-xs uppercase font-bold tracking-widest text-[#E65C00] hover:underline" data-testid="fileupload-replace">Replace</button>
                <button type="button" onClick={() => onChange("")} className="text-xs uppercase font-bold tracking-widest text-gray-500 hover:text-red-600 flex items-center gap-1"><X className="w-3 h-3" /> Remove</button>
              </div>
            </div>
          </div>
        ) : value ? (
          <div className="flex items-center gap-3">
            <div className="w-20 h-20 bg-gray-100 flex items-center justify-center border border-gray-200"><Icon className="w-7 h-7 text-gray-400" /></div>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-gray-500 truncate">{value}</div>
              <div className="flex gap-2 mt-2">
                <button type="button" onClick={() => ref.current?.click()} className="text-xs uppercase font-bold tracking-widest text-[#E65C00] hover:underline">Replace</button>
                <button type="button" onClick={() => onChange("")} className="text-xs uppercase font-bold tracking-widest text-gray-500 hover:text-red-600 flex items-center gap-1"><X className="w-3 h-3" /> Remove</button>
              </div>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => ref.current?.click()}
            disabled={uploading}
            className="w-full flex flex-col items-center justify-center gap-2 py-6 text-gray-500 hover:text-[#E65C00] transition-colors"
            data-testid="fileupload-button"
          >
            <Upload className="w-6 h-6" />
            <div className="text-xs uppercase font-bold tracking-widest">{uploading ? "Uploading..." : "Choose file or drag & drop"}</div>
            <div className="text-[10px] text-gray-400">Max 50 MB · {accept}</div>
          </button>
        )}
        <input
          ref={ref}
          type="file"
          accept={accept}
          onChange={(e) => upload(e.target.files?.[0])}
          className="hidden"
          data-testid="fileupload-input"
        />
      </div>

      {allowUrl && (
        <details className="mt-2">
          <summary className="text-[10px] uppercase tracking-widest font-bold text-gray-500 cursor-pointer hover:text-[#E65C00]">Or paste a URL</summary>
          <div className="mt-2 flex gap-2">
            <input
              type="url"
              placeholder="https://..."
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm"
              data-testid="fileupload-url-input"
            />
            <button
              type="button"
              onClick={() => { if (urlInput) { onChange(urlInput); setUrlInput(""); } }}
              className="bg-[#1A1A1A] text-white px-4 text-xs font-bold uppercase tracking-widest hover:bg-[#E65C00]"
              data-testid="fileupload-url-set"
            >
              Set
            </button>
          </div>
        </details>
      )}
    </div>
  );
}
